// © Microsoft Corporation. All rights reserved.

import { ConnectorNamespaces } from "@msnews/experiences-constants";

type DataConnectorStateCallback = (newState: any) => void;

const callbackMap: { [dataConnectorName: string]: DataConnectorStateCallback[] } = {};
let getDataConnector: (dataConnectorName: ConnectorNamespaces) => any;

/**
 * Sets up the data connector tracker. Needed to avoid a dependency cycle.
 * @param tryGetDataConnector The method to use to get the data connector.
 */
export function initializeDataConnectorTracker(tryGetDataConnector: (dataConnectorName: ConnectorNamespaces) => any) {
    getDataConnector = tryGetDataConnector;
}

/**
 * Subscribes to changes in data connector state.
 * @param dataConnectorName The name of the data connector to subscribe to.
 * @param callback The callback function to invoke when the state changes.
 * @param fireForInitialState True to fire the callback immediately when the data connector is already initialized. Defaults to true.
 */
export function subscribeDataConnectorState<TState>(dataConnectorName: ConnectorNamespaces, callback: (newState: TState) => void, fireForInitialState = true) {
    let callbackList = callbackMap[dataConnectorName];
    if (!callbackList) {
        callbackList = [];
        callbackMap[dataConnectorName] = callbackList;
    }

    callbackList.push(callback);

    if (fireForInitialState) {
        const dataConnector = getDataConnector(dataConnectorName);
        if (dataConnector) {
            const initialState = dataConnector.getCurrentState();
            if (initialState) {
                callback(initialState);
            }
        }
    }
}

/**
 * Reports a state change for a data connector to interested parties.
 * @param dataConnectorName The name of the data connector whose state changed.
 * @param newState The new state.
 */
export function reportDataConnectorStateChange(dataConnectorName: string, newState: any) {
    const callbacks: DataConnectorStateCallback[] = callbackMap[dataConnectorName];
    if (callbacks && newState) {
        for (const callback of callbacks) {
            callback(newState);
        }
    }
}